import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabase';
import { KINDS, SOURCES, kindOf, kindOrFilter, describe, groupEntries, isChange } from '../utils/activityLog';
import { exportSheetsToXlsx } from '../utils/exportXlsx';

// ═══ UTILITIES → ACTIVITY — who did what, when (Philip 2026-10-23, docs/activity-log-everything-plan.md) ═══
// Reads activity_log: the database's own record of every change to a work table (log_change()), plus the events screens
// write (scan, file, mark paid …). Readable only with "Can view Activity" (ntrg_staff.can_view_activity — the table's
// read rule; set by admins in Table Management → Employees).
// Paged (PAGE rows at a time, "Load more"), filtered on the server by person, client, parcel, dates, kind of work,
// add / edit / delete and source; grouped for reading (utils/activityLog groupEntries); each entry expands to its
// old → new values; Excel export of what is loaded.
const PAGE = 200;
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localMid = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const daysAgo = (n) => { const t = new Date(); t.setDate(t.getDate() - n); return ymd(t); };
const fmtTime = (ts) => new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const dayLabel = (ts) => {
  const d = new Date(ts); const today = new Date(); const y = new Date(); y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

export default function ActivityFeed({ darkMode, staffUser }) {
  const isDark = darkMode;
  const c = isDark ? {
    card: 'rgba(47,56,84,0.6)', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc',
    input: 'var(--ink)', inputBorder: '#434C68', th: 'rgba(26,34,56,0.5)', rowHover: 'rgba(255,255,255,0.04)',
  } : {
    card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068',
    input: '#ffffff', inputBorder: '#c8ccd8', th: 'var(--line-soft)', rowHover: 'rgba(0,0,0,0.02)',
  };
  const inp = { padding: '6px 9px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '12px', fontFamily: "'Public Sans',system-ui,sans-serif" };
  const lbl = { fontSize: '10px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' };
  const btnGhost = { padding: '6px 12px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '7px', color: c.muted, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" };
  const canView = !!staffUser?.can_view_activity;

  const [f, setF] = useState({ staff: '', client: '', parcel: '', from: daysAgo(6), to: ymd(new Date()), kind: '', op: '', source: '' });
  const [rows, setRows] = useState([]);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState({});
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const names = useRef({ props: {}, parcels: {} });
  const [, setNamesTick] = useState(0);

  useEffect(() => {
    if (!canView) return;
    supabase.from('ntrg_staff').select('id, name, active').order('name').then(({ data }) => setStaff(data || []));
    supabase.from('clients').select('id, name').order('name').then(({ data }) => setClients(data || []));
  }, [canView]);

  // Property and parcel names for the rows on screen, fetched once each.
  const fillNames = useCallback(async (list) => {
    const want = (key, col) => [...new Set(list.map((r) => r[col]).filter((id) => id && !(id in names.current[key])))];
    const propIds = want('props', 'property_id');
    const parcelIds = want('parcels', 'parcel_id');
    for (let i = 0; i < propIds.length; i += 100) {
      const { data } = await supabase.from('properties').select('id, name').in('id', propIds.slice(i, i + 100));
      (data || []).forEach((p) => { names.current.props[p.id] = p.name; });
      propIds.slice(i, i + 100).forEach((id) => { if (!(id in names.current.props)) names.current.props[id] = null; });
    }
    for (let i = 0; i < parcelIds.length; i += 100) {
      const { data } = await supabase.from('parcels').select('id, account_number').in('id', parcelIds.slice(i, i + 100));
      (data || []).forEach((p) => { names.current.parcels[p.id] = p.account_number; });
      parcelIds.slice(i, i + 100).forEach((id) => { if (!(id in names.current.parcels)) names.current.parcels[id] = null; });
    }
    setNamesTick((n) => n + 1);
  }, []);

  // One page of the log under the current filters. offset 0 = start over.
  const load = useCallback(async (offset) => {
    if (!canView) return;
    setLoading(true); setErr('');
    try {
      let q = supabase.from('activity_log').select('*').order('created_at', { ascending: false }).order('id', { ascending: false });
      if (f.from) q = q.gte('created_at', localMid(f.from).toISOString());
      if (f.to) { const end = localMid(f.to); end.setDate(end.getDate() + 1); q = q.lt('created_at', end.toISOString()); }
      if (f.staff) {
        const s = staff.find((x) => x.id === f.staff);
        // Older rows carry only the name; newer ones the staff row (matched by login).
        q = s ? q.or(`staff_id.eq.${s.id},and(staff_id.is.null,staff_user.eq."${s.name}")`) : q.eq('staff_id', f.staff);
      }
      if (f.client) {
        const cl = clients.find((x) => x.id === f.client);
        q = cl ? q.or(`client_id.eq.${cl.id},and(client_id.is.null,client_name.eq."${cl.name}")`) : q.eq('client_id', f.client);
      }
      if (f.parcel.trim()) {
        const { data: ps } = await supabase.from('parcels').select('id').ilike('account_number', `%${f.parcel.trim()}%`).limit(200);
        const ids = (ps || []).map((p) => p.id);
        if (!ids.length) { setRows([]); setMore(false); return; }
        q = q.in('parcel_id', ids);
      }
      const kf = f.kind ? kindOrFilter(f.kind) : null;
      if (kf) q = q.or(kf);
      if (f.op) q = q.eq('op', f.op);
      if (f.source) q = q.eq('source', f.source);
      const { data, error } = await q.range(offset, offset + PAGE - 1);
      if (error) throw error;
      const page = data || [];
      setRows((prev) => (offset ? [...prev, ...page] : page));
      setMore(page.length === PAGE);
      fillNames(page);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [f, staff, clients, canView, fillNames]);

  useEffect(() => { setOpen({}); load(0); }, [load]);

  if (!canView) {
    return (
      <div style={{ fontFamily: "'Public Sans',system-ui,sans-serif", color: c.text }}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', marginBottom: '6px' }}>Activity</div>
        <div style={{ fontSize: '13px', color: c.muted }}>You don’t have access to Activity. An admin can turn on <strong>Can view Activity</strong> for you in Table Management → Employees.</div>
      </div>
    );
  }

  const staffName = (r) => (r.staff_id && staff.find((s) => s.id === r.staff_id)?.name) || r.staff_user || ({ sql: 'SQL editor', script: 'Script', server: 'Server' }[r.source] || '—');
  const clientName = (r) => (r.client_id && clients.find((x) => x.id === r.client_id)?.name) || r.client_name || '';
  const where = (r) => [clientName(r), (r.property_id && names.current.props[r.property_id]) || r.property_name || '',
    (r.parcel_id && names.current.parcels[r.parcel_id]) || '', r.tax_year || ''].filter(Boolean).join(' · ');
  const sourceTag = (r) => (r.source && r.source !== 'app' ? (SOURCES.find((s) => s.key === r.source)?.label || r.source) : null);

  function exportRows() {
    const out = rows.map((r) => {
      const d = describe(r);
      return {
        when: new Date(r.created_at).toLocaleString('en-US'), who: staffName(r), from: SOURCES.find((s) => s.key === r.source)?.label || '',
        kind: kindOf(r)?.label || '', action: `${d.verb} ${d.what}`.trim(), client: clientName(r),
        property: (r.property_id && names.current.props[r.property_id]) || r.property_name || '',
        parcel: (r.parcel_id && names.current.parcels[r.parcel_id]) || '', year: r.tax_year || '',
        changes: d.changes.map((x) => `${x.column}: ${x.from ?? ''}${x.from != null && x.to != null ? ' → ' : ''}${x.to ?? ''}`).join('; '),
      };
    });
    const cols = ['when', 'who', 'from', 'kind', 'action', 'client', 'property', 'parcel', 'year', 'changes']
      .map((k) => ({ key: k, label: k[0].toUpperCase() + k.slice(1) }));
    exportSheetsToXlsx(`Activity ${f.from || ''} to ${f.to || ''}`, [{ name: 'Activity', columns: cols, rows: out }]);
  }

  const groups = groupEntries(rows);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  let lastDay = null;

  return (
    <div style={{ fontFamily: "'Public Sans',system-ui,sans-serif", color: c.text }}>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', color: c.text, marginBottom: '4px' }}>Activity</div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '16px' }}>Who did what, and when — every change to a client, parcel, value, notice, bill or document, recorded by the database.</div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '14px' }}>
        <div><div style={lbl}>Person</div>
          <select value={f.staff} onChange={(e) => set('staff', e.target.value)} style={inp}>
            <option value="">Everyone</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.name}{s.active === false ? ' (inactive)' : ''}</option>)}
          </select></div>
        <div><div style={lbl}>Client</div>
          <select value={f.client} onChange={(e) => set('client', e.target.value)} style={inp}>
            <option value="">All clients</option>
            {clients.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select></div>
        <div><div style={lbl}>Parcel</div>
          <input value={f.parcel} onChange={(e) => set('parcel', e.target.value)} placeholder="Account #" style={{ ...inp, width: '150px' }} /></div>
        <div><div style={lbl}>From</div><input type="date" value={f.from} onChange={(e) => set('from', e.target.value)} style={inp} /></div>
        <div><div style={lbl}>To</div><input type="date" value={f.to} onChange={(e) => set('to', e.target.value)} style={inp} /></div>
        <div><div style={lbl}>Kind of work</div>
          <select value={f.kind} onChange={(e) => set('kind', e.target.value)} style={inp}>
            <option value="">All work</option>
            {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select></div>
        <div><div style={lbl}>Action</div>
          <select value={f.op} onChange={(e) => set('op', e.target.value)} style={inp}>
            <option value="">Any</option><option value="add">Added</option><option value="edit">Changed</option><option value="delete">Deleted</option>
          </select></div>
        <div><div style={lbl}>From where</div>
          <select value={f.source} onChange={(e) => set('source', e.target.value)} style={inp}>
            <option value="">Anywhere</option>
            {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select></div>
        <button type="button" onClick={exportRows} disabled={!rows.length} style={{ ...btnGhost, opacity: rows.length ? 1 : 0.5 }}>⬇ Excel</button>
      </div>

      {err && <div style={{ color: 'var(--red)', fontSize: '12px', marginBottom: '10px' }}>Could not load the activity log: {err}</div>}

      <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        {!groups.length && !loading && <div style={{ padding: '28px', textAlign: 'center', color: c.muted, fontSize: '13px' }}>No activity for these filters.</div>}
        {groups.map((g) => {
          const r = g.rows[0];
          const day = dayLabel(r.created_at);
          const dayHead = day !== lastDay ? (lastDay = day) : null;
          const d = describe(r);
          const kinds = [...new Set(g.rows.map((x) => describe(x).what))];
          const isOpen = !!open[g.key + r.id];
          const tag = sourceTag(r);
          return (
            <React.Fragment key={g.key + r.id}>
              {dayHead && <div style={{ padding: '8px 16px', fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', background: c.th, borderBottom: `1px solid ${c.border}` }}>{dayHead}</div>}
              <div onClick={() => setOpen((p) => ({ ...p, [g.key + r.id]: !isOpen }))}
                style={{ padding: '10px 16px', borderBottom: `1px solid ${c.border}`, cursor: 'pointer', display: 'grid', gridTemplateColumns: '64px 1fr', gap: '10px' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = c.rowHover; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                <div style={{ fontSize: '12px', color: c.muted, fontFamily: "'DM Mono',monospace" }}>{fmtTime(r.created_at)}</div>
                <div>
                  <div style={{ fontSize: '13px', color: c.text }}>
                    <strong>{staffName(r)}</strong>{' '}
                    {g.rows.length > 1
                      ? <>made <strong>{g.rows.length} changes</strong> <span style={{ color: c.sub }}>({kinds.slice(0, 4).join(', ')}{kinds.length > 4 ? ', …' : ''})</span></>
                      : <>{d.verb} <span style={{ color: c.sub }}>{d.what}</span></>}
                    {tag && <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: '700', color: c.muted, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '1px 6px' }}>{r.batch && r.source === 'script' ? r.batch : tag}</span>}
                    <span style={{ marginLeft: '8px', fontSize: '11px', color: c.muted }}>{isOpen ? '▾' : '▸'}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: c.muted, marginTop: '2px' }}>{where(r) || kindOf(r)?.label || ''}</div>
                  {!isOpen && g.rows.length === 1 && d.changes.length > 0 && (
                    <div style={{ fontSize: '11px', color: c.sub, marginTop: '3px' }}>
                      {d.changes.slice(0, 2).map((x) => `${x.column}: ${x.from ?? ''}${x.from != null && x.to != null ? ' → ' : ''}${x.to ?? ''}`).join(' · ')}{d.changes.length > 2 ? ' · …' : ''}
                    </div>
                  )}
                  {isOpen && (
                    <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
                      {g.rows.map((x) => {
                        const dx = describe(x);
                        return (
                          <div key={x.id} style={{ fontSize: '12px', borderLeft: `2px solid ${c.border}`, paddingLeft: '10px' }}>
                            <div style={{ color: c.text }}><span style={{ color: c.muted, fontFamily: "'DM Mono',monospace" }}>{fmtTime(x.created_at)}</span> · {dx.verb} {dx.what}
                              {g.rows.length > 1 && where(x) !== where(r) && <span style={{ color: c.muted }}> · {where(x)}</span>}</div>
                            {dx.changes.map((ch) => (
                              <div key={ch.column} style={{ color: c.sub, fontSize: '11px', paddingLeft: '8px' }}>
                                {ch.column}: {ch.from != null && <span style={{ textDecoration: ch.to != null ? 'line-through' : 'none', color: c.muted }}>{ch.from}</span>}
                                {ch.from != null && ch.to != null && ' → '}{ch.to != null && <strong style={{ color: c.text, fontWeight: 600 }}>{ch.to}</strong>}
                              </div>
                            ))}
                            {!isChange(x) && x.details && <div style={{ color: c.muted, fontSize: '11px', paddingLeft: '8px' }}>{JSON.stringify(x.details).slice(0, 200)}</div>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
        {loading && <div style={{ padding: '16px', textAlign: 'center', color: c.muted, fontSize: '12px' }}>Loading…</div>}
      </div>
      {more && !loading && (
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <button type="button" onClick={() => load(rows.length)} style={btnGhost}>Load more</button>
        </div>
      )}
      <div style={{ fontSize: '11px', color: c.muted, marginTop: '10px' }}>{rows.length} log rows loaded{more ? ' — more available' : ''}.</div>
    </div>
  );
}
